<template>
  <v-btn block variant="outlined" size="large" class="google-button"
         :loading="working" @click="signIn()">
    <v-icon start>mdi-google</v-icon>
    {{ $tt('auth.google') }}
  </v-btn>
</template>

<script lang="ts">
import {Component, Vue, toNative} from 'vue-facing-decorator'
import {auth} from '@/firebase'
import {EmailNotVerifiedError} from '@/errors/errors'
import {notApproved} from '@/types/Access'
import {AuthRefusal} from '@/utils/authRefusal'
import {saveErrorMessage} from '@/utils/saveErrors'

/**
 * Entrar com o Google, e o que fazer quando não dá.
 *
 * Componente, e não um botão repetido em cada tela, porque **entrar e cadastrar-se pelo Google
 * são a mesma coisa**: não há dois caminhos, há um — a conta nasce no primeiro clique. As duas
 * telas usam este mesmo botão e só decidem para onde ir depois.
 *
 * Aqui mora também a tradução dos erros do provedor, que é o que não se quer escrito duas vezes.
 */
@Component({name: 'ontos-google-sign-in', emits: ['signed', 'failed']})
class GoogleSignIn extends Vue {
  working = false

  async signIn() {
    this.working = true
    try {
      await this.$auth.signInWithGoogle()
      this.$emit('signed')
    } catch (err: any) {
      //Três respostas possíveis: `null` é "não é assunto meu" e sobe; vazio é desistência, que
      //não merece aviso; e o resto é frase para a tela mostrar
      const message = this.messageFor(err)
      if (message === null) throw err
      //A recusa inteira, e não só a frase: o e-mail da conta escolhida e se o convite espera
      //aprovação. O `AuthRefusal` diz por quê — e é a tela que decide o que fazer com ela.
      if (message) this.$emit('failed', {
        message,
        email: auth.currentUser?.email || '',
        notApproved: notApproved(err)
      } as AuthRefusal)
    } finally {
      this.working = false
    }
  }

  /**
   * A frase para o que deu errado, ou nada quando não é assunto deste botão.
   *
   * **São duas origens, e as duas param aqui**: o provedor, antes do token, e o servidor, no
   * `sync` que vem logo depois. Entrar pelo Google é um gesto só para quem clica, e dividir a
   * explicação entre o componente e a tela faria a metade de baixo chegar como "erro
   * inesperado" — foi exatamente o que aconteceu com um convite ainda não aprovado.
   *
   * **Fechar o popup não é erro**: é desistir, e desistir não merece aviso vermelho. Os três
   * códigos de desistência saem em silêncio.
   *
   * O caso de provedor que mais confunde é o terceiro: quem já tem conta por senha com aquele
   * e-mail não entra pelo Google até ligar as duas.
   *
   * As recusas do servidor saem do `saveErrorMessage`, que é o dono único de traduzir o
   * `validation` — é por ele que chega "Seu convite ainda não foi aprovado".
   */
  private messageFor(err: any): string | null {
    const code = err?.code
    if (code) {
      if (['auth/popup-closed-by-user', 'auth/cancelled-popup-request',
           'auth/user-cancelled'].includes(code)) return ''
      if (code === 'auth/account-exists-with-different-credential')
        return this.$tt('auth.account-exists-password') as string
      if (code === 'auth/popup-blocked') return this.$tt('auth.popup-blocked') as string
      if (String(code).startsWith('auth/')) return this.$tt('auth.generic-error') as string
    }
    //O e-mail não verificado vem sem `validation`: ele é um 403 com `type`, e o cliente já o
    //transforma na sua própria classe
    if (err instanceof EmailNotVerifiedError) return this.$tt('auth.email-not-verified') as string
    //O convite tem frase própria AQUI, e não a do `saveErrorMessage`: pelo Google a conta nasce
    //no primeiro clique, então quem vê esta recusa acabou de PEDIR o convite — nas duas telas,
    //porque pelo provedor entrar e cadastrar-se são o mesmo gesto. "Ainda não foi aprovado"
    //responde a quem tentou ENTRAR com uma conta que já tinha, e é o que o login por senha diz.
    if (notApproved(err)) return this.$tt('auth.invitation-requested') as string
    return saveErrorMessage(err)
  }
}

export default toNative(GoogleSignIn)
</script>

<style scoped>
/* Caixa alta é a voz dos botões do Vuetify, e aqui o rótulo é uma frase — "Entrar com o Google"
   em versais vira grito na primeira coisa que alguém lê do produto. */
.google-button {
  letter-spacing: normal;
  text-transform: none;
}
</style>
